import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useWishlist } from '@/contexts/WishlistContext';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Filter, Heart } from 'lucide-react';
import { toast } from 'sonner';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const navigate = useNavigate();
  
  const category = searchParams.get('category');
  const subcategory = searchParams.get('subcategory');
  const priceRange = searchParams.get('price_range');
  const fabric = searchParams.get('fabric');
  const sort = searchParams.get('sort') || '';
  const q = searchParams.get('q') || '';
  const [fabricsList, setFabricsList] = useState([]);

  useEffect(() => {
    fetchProducts();
  }, [category, subcategory, priceRange, fabric, sort, q]);

  useEffect(() => {
    const fetchFabrics = async () => {
      try {
        const response = await api.get('/fabrics');
        setFabricsList(response.data);
      } catch (error) { console.error(error); }
    };
    fetchFabrics();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (subcategory) params.append('subcategory', subcategory);
      if (priceRange) params.append('price_range', priceRange);
      if (fabric) params.append('fabric', fabric);
      if (sort) params.append('sort', sort);
      if (q) params.append('q', q);
      
      const response = await api.get(`/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'default') {
      newParams.set('sort', value);
    } else {
      newParams.delete('sort');
    }
    setSearchParams(newParams);
  };

  const handlePriceRangeChange = (value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      newParams.set('price_range', value);
    } else {
      newParams.delete('price_range');
    }
    setSearchParams(newParams);
  };

  const handleFabricChange = (value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      newParams.set('fabric', value);
    } else {
      newParams.delete('fabric');
    }
    setSearchParams(newParams);
  };

  const handleSubcategoryChange = (value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      newParams.set('subcategory', value);
    } else {
      newParams.delete('subcategory');
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    const newParams = new URLSearchParams();
    if (category) newParams.set('category', category);
    setSearchParams(newParams);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="products-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="py-24 md:py-32 max-w-[1400px] mx-auto px-4 md:px-8">
      <div className="mb-12">
        <h1 className="text-5xl md:text-6xl font-light tracking-tight mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="products-page-title">
          {q ? `Search: "${q}"` : (category || 'All Products')}
        </h1>
        <p className="text-lg text-muted-foreground font-light">
          {q ? `${products.length} result${products.length === 1 ? '' : 's'} found` : 'Handcrafted with love and tradition'}
        </p>
      </div>

      {/* Filters and Sorting */}
      <div className="mb-12 flex flex-wrap gap-4 items-center justify-between border-y border-border py-6">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm uppercase tracking-widest font-bold text-muted-foreground">Filters</span>
        </div>
        
        <div className="flex flex-wrap gap-4">
          {category === 'Sarees' && (
            <Select value={subcategory || 'all'} onValueChange={handleSubcategoryChange}>
              <SelectTrigger className="w-[180px] rounded-none" data-testid="subcategory-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Banarasi">Banarasi</SelectItem>
                <SelectItem value="Silk">Silk</SelectItem>
                <SelectItem value="Cotton">Cotton</SelectItem>
                <SelectItem value="Georgette">Georgette</SelectItem>
                <SelectItem value="Chiffon">Chiffon</SelectItem>
              </SelectContent>
            </Select>
          )}
          
          <Select value={priceRange || 'all'} onValueChange={handlePriceRangeChange}>
            <SelectTrigger className="w-[200px] rounded-none" data-testid="price-range-filter">
              <SelectValue placeholder="All Prices" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Prices</SelectItem>
              <SelectItem value="0-5000">Under ₹5,000</SelectItem>
              <SelectItem value="5000-15000">₹5,000 - ₹15,000</SelectItem>
              <SelectItem value="15000-30000">₹15,000 - ₹30,000</SelectItem>
              <SelectItem value="30000-50000">₹30,000 - ₹50,000</SelectItem>
              <SelectItem value="50000-100000">₹50,000 - ₹1,00,000</SelectItem>
              <SelectItem value="100000-999999">Above ₹1,00,000</SelectItem>
            </SelectContent>
          </Select>
          <Select value={fabric || 'all'} onValueChange={handleFabricChange}>
            <SelectTrigger className="w-[180px] rounded-none" data-testid="fabric-filter">
              <SelectValue placeholder="All Fabrics" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Fabrics</SelectItem>
              {fabricsList.map((fab) => (
                <SelectItem key={fab.id} value={fab.name}>{fab.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={sort || 'default'} onValueChange={handleSortChange}>
            <SelectTrigger className="w-[200px] rounded-none" data-testid="sort-select">
              <SelectValue placeholder="Sort By" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Default</SelectItem>
              <SelectItem value="price_asc">Price: Low to High</SelectItem>
              <SelectItem value="price_desc">Price: High to Low</SelectItem>
              <SelectItem value="best_seller">Best Seller</SelectItem>
              <SelectItem value="designers_choice">Designer's Choice</SelectItem>
            </SelectContent>
          </Select>

          {(subcategory || priceRange || fabric || sort) && (
            <Button 
              variant="outline" 
              onClick={clearFilters}
              className="rounded-none"
              data-testid="clear-filters-button"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {products.length === 0 ? (
        <div className="text-center py-12" data-testid="no-products-message">
          <p className="text-lg text-muted-foreground">No products available matching your filters.</p>
          <Button onClick={clearFilters} className="mt-4 rounded-none px-6 py-4 uppercase tracking-widest text-xs">
            Clear Filters
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-12 gap-x-8" data-testid="products-grid">
          {products.map((product) => (
            <div 
              key={product.id} 
              className="group relative"
              data-testid={`product-card-${product.id}`}
            >
              <Link to={`/products/${product.id}`}>
                <div className="relative overflow-hidden mb-4 h-[400px] rounded-xl">
                  <img 
                    src={product.image || 'https://placehold.co/600x800/f5f5f4/a8a29e?text=No+Image'} 
                    alt={product.name} 
                    onError={(e) => { e.currentTarget.src = 'https://placehold.co/600x800/f5f5f4/a8a29e?text=Image+Unavailable'; }}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 rounded-xl"
                  />
                  {product.stock === 0 && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-xl">
                      <span className="text-white text-sm uppercase tracking-widest">Out of Stock</span>
                    </div>
                  )}
                  {product.tags && product.tags.includes('best_seller') && (
                    <div className="absolute top-4 left-4 bg-accent text-accent-foreground px-3 py-1 text-xs uppercase tracking-widest font-bold">
                      Best Seller
                    </div>
                  )}
                  {product.tags && product.tags.includes('designers_choice') && (
                    <div className="absolute top-4 left-4 bg-primary text-primary-foreground px-3 py-1 text-xs uppercase tracking-widest font-bold">
                      Designer's Choice
                    </div>
                  )}
                </div>
                <h3 className="text-xl font-medium mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {product.name}
                </h3>
                {product.fabric && (
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{product.fabric}</p>
                )}
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{product.description}</p>
                <p className="text-lg font-light">₹{product.price.toLocaleString('en-IN')}</p>
              </Link>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  if (!user) { toast.error('Please login to add to wishlist'); navigate('/login'); return; }
                  toggleWishlist(product.id).then(ok => {
                    if (ok) toast.success(isWishlisted(product.id) ? 'Removed from wishlist' : 'Added to wishlist');
                  });
                }}
                className="absolute top-4 right-4 bg-white/90 hover:bg-white rounded-full p-2.5 shadow-md transition-all z-10 hover:scale-110"
                data-testid={`wishlist-btn-${product.id}`}
              >
                <Heart className={`h-5 w-5 transition-colors ${isWishlisted(product.id) ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Products;